import { Alert, AnimatedContentLoader, Button } from '@components'
import { useBusinessContext } from '@context'
import { IncomesRegisterRow, downloadIncomesRegisterCsv, fetchIncomesRegister } from '@query'
import { formatEurosWithOpts } from '@utils/formatCurrency'
import { useQuery } from '@tanstack/react-query'
import React, { useEffect, useMemo, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'
import {
  FaCalendarAlt,
  FaCaretDown,
  FaChevronLeft,
  FaChevronRight,
  FaDownload
} from 'react-icons/fa'
import styled, { css } from 'styled-components'

const MONEY_FIELDS = [
  'mileage',
  'domestic_per_diem',
  'foreign_per_diem',
  'meal_allowance',
  'total'
] as const

type MoneyField = (typeof MONEY_FIELDS)[number]

const currentMonth = () => {
  const now = new Date()
  return monthValue(now.getFullYear(), now.getMonth())
}

const monthValue = (year: number, monthIndex: number) => {
  const date = new Date(year, monthIndex, 1)
  const month = date.getMonth() + 1
  return `${date.getFullYear()}-${month < 10 ? `0${month}` : month}`
}

const shiftMonth = (month: string, delta: number) => {
  const [year, monthNumber] = month.split('-').map(Number)
  return monthValue(year, monthNumber - 1 + delta)
}

const formatMonth = (month: string, language: string, monthStyle: 'long' | 'short') => {
  const [year, monthNumber] = month.split('-').map(Number)
  const options: Intl.DateTimeFormatOptions =
    monthStyle === 'long' ? { month: 'long', year: 'numeric' } : { month: 'short' }
  const formatted = new Intl.DateTimeFormat(language, options).format(
    new Date(year, monthNumber - 1, 1)
  )
  return formatted.charAt(0).toLocaleUpperCase(language) + formatted.slice(1)
}

const sumRows = (rows: IncomesRegisterRow[]): Record<MoneyField, number> =>
  rows.reduce(
    (totals, row) => {
      MONEY_FIELDS.forEach(field => {
        totals[field] += row[field]
      })
      return totals
    },
    { mileage: 0, domestic_per_diem: 0, foreign_per_diem: 0, meal_allowance: 0, total: 0 }
  )

export const IncomesRegisterTab: React.FC = () => {
  const [t, i18n] = useTranslation()
  const { slug } = useBusinessContext()
  const [month, setMonth] = useState(currentMonth)
  const [pickerOpen, setPickerOpen] = useState(false)
  const [pickerYear, setPickerYear] = useState(() => Number(currentMonth().slice(0, 4)))
  const [downloadFailed, setDownloadFailed] = useState(false)
  const [downloading, setDownloading] = useState(false)
  const pickerRef = useRef<HTMLDivElement>(null)

  const report = useQuery({
    queryKey: ['expense-claims', 'incomes-register', slug, month],
    queryFn: () => fetchIncomesRegister(slug, month),
    enabled: month.length > 0
  })

  useEffect(() => {
    if (!pickerOpen) return
    const onPointer = (event: MouseEvent) => {
      if (!pickerRef.current?.contains(event.target as Node)) setPickerOpen(false)
    }
    const onKey = (event: KeyboardEvent) => {
      if (event.key === 'Escape') setPickerOpen(false)
    }
    document.addEventListener('mousedown', onPointer)
    document.addEventListener('keydown', onKey)
    return () => {
      document.removeEventListener('mousedown', onPointer)
      document.removeEventListener('keydown', onKey)
    }
  }, [pickerOpen])

  const rows = report.data?.rows ?? []
  const totals = useMemo(() => sumRows(rows), [rows])
  const language = i18n.language
  const columns: { field: MoneyField; label: string }[] = [
    { field: 'mileage', label: t('travelAndExpenses.incomes.columns.mileage') },
    { field: 'domestic_per_diem', label: t('travelAndExpenses.incomes.columns.domesticPerDiem') },
    { field: 'foreign_per_diem', label: t('travelAndExpenses.incomes.columns.foreignPerDiem') },
    { field: 'meal_allowance', label: t('travelAndExpenses.incomes.columns.mealAllowance') },
    { field: 'total', label: t('travelAndExpenses.incomes.columns.total') }
  ]

  const download = async () => {
    setDownloading(true)
    setDownloadFailed(false)
    try {
      await downloadIncomesRegisterCsv(slug, month)
    } catch {
      setDownloadFailed(true)
    } finally {
      setDownloading(false)
    }
  }

  return (
    <Stack>
      <SummaryCard>
        <MonthBlock ref={pickerRef}>
          <SectionLabel>{t('travelAndExpenses.incomes.paymentMonth')}</SectionLabel>
          <MonthControls>
            <IconButton
              type="button"
              aria-label={t('travelAndExpenses.incomes.previousMonth')}
              onClick={() => setMonth(current => shiftMonth(current, -1))}
            >
              <FaChevronLeft />
            </IconButton>
            <MonthButton
              type="button"
              aria-haspopup="dialog"
              aria-expanded={pickerOpen}
              onClick={() => {
                setPickerYear(Number(month.slice(0, 4)))
                setPickerOpen(open => !open)
              }}
            >
              <FaCalendarAlt />
              {formatMonth(month, language, 'long')}
              <FaCaretDown />
            </MonthButton>
            <IconButton
              type="button"
              aria-label={t('travelAndExpenses.incomes.nextMonth')}
              onClick={() => setMonth(current => shiftMonth(current, 1))}
            >
              <FaChevronRight />
            </IconButton>
          </MonthControls>
          {pickerOpen && (
            <Picker role="dialog" aria-label={t('travelAndExpenses.incomes.paymentMonth')}>
              <YearRow>
                <IconButton
                  type="button"
                  aria-label={t('travelAndExpenses.incomes.previousYear')}
                  onClick={() => setPickerYear(year => year - 1)}
                >
                  <FaChevronLeft />
                </IconButton>
                <YearLabel>{pickerYear}</YearLabel>
                <IconButton
                  type="button"
                  aria-label={t('travelAndExpenses.incomes.nextYear')}
                  onClick={() => setPickerYear(year => year + 1)}
                >
                  <FaChevronRight />
                </IconButton>
              </YearRow>
              <MonthGrid>
                {Array.from({ length: 12 }, (_, index) => {
                  const value = monthValue(pickerYear, index)
                  const selected = value === month
                  return (
                    <MonthChoice
                      key={value}
                      type="button"
                      aria-pressed={selected}
                      $selected={selected}
                      onClick={() => {
                        setMonth(value)
                        setPickerOpen(false)
                      }}
                    >
                      {formatMonth(value, language, 'short')}
                    </MonthChoice>
                  )
                })}
              </MonthGrid>
            </Picker>
          )}
        </MonthBlock>
        <Stats>
          <Stat>
            <StatLabel>{t('travelAndExpenses.incomes.people')}</StatLabel>
            <StatValue>{report.isSuccess ? rows.length : '–'}</StatValue>
          </Stat>
          <Stat>
            <StatLabel>{t('travelAndExpenses.incomes.paidClaims')}</StatLabel>
            <StatValue>{report.isSuccess ? report.data.claim_count : '–'}</StatValue>
          </Stat>
          <Stat>
            <StatLabel>{t('travelAndExpenses.incomes.columns.total')}</StatLabel>
            <StatValue>{report.isSuccess ? formatEurosWithOpts(totals.total) : '–'}</StatValue>
          </Stat>
        </Stats>
        <DownloadButton
          type="button"
          icon={<FaDownload />}
          showSpinner={downloading}
          onClick={download}
        >
          {t('travelAndExpenses.incomes.download')}
        </DownloadButton>
      </SummaryCard>

      <Alert
        type="info"
        title={t('travelAndExpenses.incomes.title')}
        description={t('travelAndExpenses.incomes.notice')}
        marginBottom={0}
      />
      {report.isError && <Alert type="error" title={t('travelAndExpenses.incomes.loadError')} />}
      {downloadFailed && (
        <Alert type="error" title={t('travelAndExpenses.incomes.downloadError')} />
      )}

      {!report.isError && (
        <TableCard>
          <AnimatedContentLoader isLoading={report.isLoading}>
            {report.isSuccess && rows.length === 0 ? (
              <Empty>{t('travelAndExpenses.incomes.empty')}</Empty>
            ) : (
              <TableScroll>
                <Header>
                  <span>{t('travelAndExpenses.incomes.columns.person')}</span>
                  {columns.map(column => (
                    <span key={column.field} className="end">
                      {column.label}
                    </span>
                  ))}
                </Header>
                {rows.map(row => (
                  <Row key={row.claimant_id}>
                    <Person>{row.name}</Person>
                    {columns.map(column => (
                      <Amount key={column.field} $strong={column.field === 'total'}>
                        {formatEurosWithOpts(row[column.field])}
                      </Amount>
                    ))}
                  </Row>
                ))}
                {rows.length > 0 && (
                  <TotalRow>
                    <span>{t('travelAndExpenses.incomes.columns.total')}</span>
                    {columns.map(column => (
                      <Amount key={column.field} $strong>
                        {formatEurosWithOpts(totals[column.field])}
                      </Amount>
                    ))}
                  </TotalRow>
                )}
              </TableScroll>
            )}
          </AnimatedContentLoader>
        </TableCard>
      )}
    </Stack>
  )
}

const reportGrid = css`
  display: grid;
  grid-template-columns: minmax(10rem, 1.6fr) repeat(5, minmax(6.5rem, 1fr));
  gap: ${({ theme }) => theme.spacing.px12}rem;
  align-items: center;
  padding: ${({ theme }) => theme.spacing.md}rem ${({ theme }) => theme.spacing.lg}rem;
`

const Stack = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${({ theme }) => theme.spacing.md}rem;
  min-width: 0;
  max-width: 100%;
`

const SummaryCard = styled.section`
  display: grid;
  grid-template-columns: minmax(0, 1fr) auto minmax(0, 1fr);
  align-items: end;
  gap: ${({ theme }) => theme.spacing.md}rem ${({ theme }) => theme.spacing.lg}rem;
  padding: ${({ theme }) => theme.spacing.lg}rem;
  background: ${({ theme }) => theme.colors.neutralWhite};
  border: 1px solid ${({ theme }) => theme.colors.borderSoft};
  border-radius: ${({ theme }) => theme.borderRadius.md}rem;
  min-width: 0;
  max-width: 100%;

  @media (max-width: 64em) {
    grid-template-columns: 1fr;
    align-items: stretch;
  }
`

const DownloadButton = styled(Button)`
  justify-self: end;

  @media (max-width: 64em) {
    justify-self: start;
  }
`

const SectionLabel = styled.span`
  display: block;
  margin-bottom: ${({ theme }) => theme.spacing.xs}rem;
  font-size: ${({ theme }) => theme.fontSize.xs}rem;
  font-weight: 600;
  line-height: 1.4;
  letter-spacing: ${({ theme }) => theme.typography.letterSpacing.caps};
  text-transform: uppercase;
  color: ${({ theme }) => theme.colors.iconGray};
`

const StatLabel = styled.span`
  display: flex;
  align-items: flex-end;
  justify-content: center;
  min-height: ${({ theme }) => theme.size.controlHeight}rem;
  margin-bottom: ${({ theme }) => theme.spacing.xs}rem;
  font-size: ${({ theme }) => theme.fontSize.xs}rem;
  font-weight: 600;
  line-height: 1.3;
  letter-spacing: ${({ theme }) => theme.typography.letterSpacing.caps};
  text-align: center;
  text-transform: uppercase;
  color: ${({ theme }) => theme.colors.iconGray};
`

const MonthBlock = styled.div`
  position: relative;
  display: flex;
  flex-direction: column;
  min-width: 0;
  justify-self: start;
`

const MonthControls = styled.div`
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: ${({ theme }) => theme.spacing.xs}rem;
  max-width: 100%;
`

const IconButton = styled.button`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: ${({ theme }) => theme.size.controlHeight}rem;
  height: ${({ theme }) => theme.size.controlHeight}rem;
  padding: 0;
  color: ${({ theme }) => theme.colors.metalGray};
  background: ${({ theme }) => theme.colors.neutralWhite};
  border: 1px solid ${({ theme }) => theme.colors.borderSoft};
  border-radius: ${({ theme }) => theme.borderRadius.pill}px;
  cursor: pointer;

  &:hover {
    background: ${({ theme }) => theme.colors.defaultHover};
  }
`

const MonthButton = styled.button`
  display: inline-flex;
  align-items: center;
  gap: ${({ theme }) => theme.spacing.xs}rem;
  min-height: ${({ theme }) => theme.size.controlHeight}rem;
  padding: 0 ${({ theme }) => theme.spacing.sm}rem;
  font-size: ${({ theme }) => theme.fontSize.md}rem;
  font-weight: 600;
  line-height: 1.25;
  color: ${({ theme }) => theme.colors.neutralBlack};
  background: ${({ theme }) => theme.colors.neutralWhite};
  border: 1px solid ${({ theme }) => theme.colors.borderSoft};
  border-radius: ${({ theme }) => theme.borderRadius.md}rem;
  cursor: pointer;

  svg:first-of-type {
    color: ${({ theme }) => theme.colors.primary};
    font-size: ${({ theme }) => theme.fontSize.sm}rem;
  }

  svg:last-of-type {
    color: ${({ theme }) => theme.colors.iconGray};
    font-size: ${({ theme }) => theme.fontSize.xs}rem;
  }

  &:hover {
    background: ${({ theme }) => theme.colors.defaultHover};
  }
`

const Picker = styled.div`
  position: absolute;
  top: calc(100% + ${({ theme }) => theme.spacing.xs}rem);
  left: 0;
  z-index: 2;
  width: ${({ theme }) => theme.layout.monthPickerWidth}rem;
  max-width: 100%;
  padding: ${({ theme }) => theme.spacing.sm}rem;
  background: ${({ theme }) => theme.colors.neutralWhite};
  border: 1px solid ${({ theme }) => theme.colors.borderSoft};
  border-radius: ${({ theme }) => theme.borderRadius.md}rem;
  box-shadow: ${({ theme }) => theme.shadow.md};
`

const YearRow = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: ${({ theme }) => theme.spacing.xs}rem;
`

const YearLabel = styled.span`
  font-weight: 700;
  color: ${({ theme }) => theme.colors.neutralBlack};
`

const MonthGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: ${({ theme }) => theme.spacing.xxs}rem;
`

const MonthChoice = styled.button<{ $selected: boolean }>`
  padding: ${({ theme }) => theme.spacing.xs}rem 0;
  font-size: ${({ theme }) => theme.fontSize.sm}rem;
  font-weight: ${({ $selected }) => ($selected ? 700 : 500)};
  color: ${({ theme, $selected }) =>
    $selected ? theme.colors.neutralWhite : theme.colors.neutralBlack};
  background: ${({ theme, $selected }) =>
    $selected ? theme.colors.primary : theme.colors.defaultHover};
  border: none;
  border-radius: ${({ theme }) => theme.borderRadius.sm}rem;
  cursor: pointer;
`

const Stats = styled.div`
  display: grid;
  grid-template-columns: repeat(3, minmax(5.5rem, max-content));
  column-gap: ${({ theme }) => theme.spacing.xl}rem;
  align-items: end;
  justify-self: center;

  @media (max-width: 64em) {
    justify-self: stretch;
    width: 100%;
    grid-template-columns: repeat(3, minmax(0, 1fr));
    column-gap: ${({ theme }) => theme.spacing.sm}rem;
  }
`

const Stat = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  text-align: center;
`

const StatValue = styled.span`
  font-size: ${({ theme }) => theme.fontSize.lg}rem;
  font-weight: 700;
  line-height: 1.2;
  font-variant-numeric: tabular-nums;
  color: ${({ theme }) => theme.colors.neutralBlack};
`

const TableCard = styled.div`
  overflow-x: auto;
  min-width: 0;
  max-width: 100%;
  background: ${({ theme }) => theme.colors.neutralWhite};
  border: 1px solid ${({ theme }) => theme.colors.borderSoft};
  border-radius: ${({ theme }) => theme.borderRadius.md}rem;
`

const TableScroll = styled.div`
  min-width: ${({ theme }) => theme.layout.incomesRegisterScrollMin}rem;
`

const Header = styled.div`
  ${reportGrid}
  font-size: ${({ theme }) => theme.fontSize.xs}rem;
  font-weight: 600;
  letter-spacing: ${({ theme }) => theme.typography.letterSpacing.capsTight};
  text-transform: uppercase;
  color: ${({ theme }) => theme.colors.iconGray};
  background: ${({ theme }) => theme.colors.defaultHover};
  border-bottom: 1px solid ${({ theme }) => theme.colors.neutralGray};

  .end {
    text-align: right;
  }
`

const Row = styled.div`
  ${reportGrid}
  font-size: ${({ theme }) => theme.fontSize.sm}rem;
  border-bottom: 1px solid ${({ theme }) => theme.colors.neutralGray};
`

const TotalRow = styled.div`
  ${reportGrid}
  font-size: ${({ theme }) => theme.fontSize.sm}rem;
  font-weight: 800;
  color: ${({ theme }) => theme.colors.neutralBlack};
`

const Person = styled.span`
  font-weight: 600;
  color: ${({ theme }) => theme.colors.neutralBlack};
`

const Amount = styled.span<{ $strong?: boolean }>`
  justify-self: end;
  font-weight: ${({ $strong }) => ($strong ? 700 : 400)};
  font-variant-numeric: tabular-nums;
  color: ${({ theme }) => theme.colors.neutralBlack};
`

const Empty = styled.p`
  margin: 0;
  padding: ${({ theme }) => theme.spacing.xl}rem;
  text-align: center;
  color: ${({ theme }) => theme.colors.iconGray};
`
